import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parse, isValid } from 'date-fns';
import { Mail, ArrowLeft, Palette, X } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { calculateWeeksLived, calculateNextBirthdayAge } from '@/components/LifeInWeeksGrid';
import PrintableCardV2 from '@/components/PrintableCardV2';
import { Toaster, toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Color options for accent
const COLOR_OPTIONS = [
  { id: 'black', label: 'Classic Black', color: '#000000' },
  { id: 'navy', label: 'Modern Navy', color: '#1a3a5c' },
  { id: 'red', label: 'Bold Red', color: '#8b1a1a' },
];

// Age-based quote suggestions
const QUOTES_BY_AGE = {
  '1': { quote: "A journey of a thousand miles begins with a single step.", author: "Lao Tzu" },
  '2': { quote: "Two years of wonder, a lifetime of possibilities.", author: "Unknown" },
  '3': { quote: "The more that you read, the more things you will know.", author: "Dr. Seuss" },
  '4': { quote: "Every child is an artist.", author: "Pablo Picasso" },
  '5': { quote: "Five years of magic, and the adventure is just beginning.", author: "Unknown" },
  '6': { quote: "Today you are you! That is truer than true!", author: "Dr. Seuss" },
  '7': { quote: "Seven is heaven — old enough to dream, young enough to believe them all.", author: "Unknown" },
  '8': { quote: "Be curious, not judgmental.", author: "Walt Whitman" },
  '9': { quote: "Almost double digits! The world better watch out.", author: "Unknown" },
  '10': { quote: "Double digits! You're officially a big deal now.", author: "Unknown" },
  '11': { quote: "You're off to great places. Today is your day!", author: "Dr. Seuss" },
  '12': { quote: "Twelve: the last year of childhood. Make it magical.", author: "Unknown" },
  '13': { quote: "Welcome to the teenage years. May they be as bold as you are.", author: "Unknown" },
  '14': { quote: "You have brains in your head. You have feet in your shoes.", author: "Dr. Seuss" },
  '15': { quote: "Fifteen: halfway to thirty, but with none of the worries.", author: "Unknown" },
  '16': { quote: "The future belongs to those who believe in the beauty of their dreams.", author: "Eleanor Roosevelt" },
  '17': { quote: "Seventeen: old enough to know better, young enough not to care.", author: "Unknown" },
  '18': { quote: "Adulthood begins. The world is yours for the taking.", author: "Unknown" },
  '19': { quote: "The last teen year. You've only just begun.", author: "Unknown" },
  '20': { quote: "The young do not know enough to be prudent, and therefore they attempt the impossible — and achieve it.", author: "Pearl S. Buck" },
  '21': { quote: "Twenty-one: old enough to know better, young enough to do it anyway.", author: "Unknown" },
  '22': { quote: "The world is yours. Everything is waiting for you.", author: "Unknown" },
  '23': { quote: "Nobody tells beginners that all of us who do creative work, we get into it because we have good taste.", author: "Ira Glass" },
  '24': { quote: "You don't have to have it all figured out to move forward.", author: "Unknown" },
  '25': { quote: "A quarter century down. The best is yet to come.", author: "Unknown" },
  '26': { quote: "Your twenties are for learning. Your thirties are for living.", author: "Unknown" },
  '27': { quote: "At 27, you're old enough to know what you want and young enough to go get it.", author: "Unknown" },
  '28': { quote: "The question isn't who is going to let me; it's who is going to stop me.", author: "Ayn Rand" },
  '29': { quote: "The last year of your twenties. Make it count.", author: "Unknown" },
  '30': { quote: "At 30, you finally become the person you were always meant to be.", author: "Unknown" },
  '31': { quote: "The best time to plant a tree was 20 years ago. The second best time is now.", author: "Chinese Proverb" },
  '32': { quote: "Do not go where the path may lead, go instead where there is no path and leave a trail.", author: "Ralph Waldo Emerson" },
  '33': { quote: "The only way to do great work is to love what you do.", author: "Steve Jobs" },
  '34': { quote: "Life shrinks or expands in proportion to one's courage.", author: "Anaïs Nin" },
  '35': { quote: "Thirty-five is a very attractive age. London society is full of women of the very highest birth who have remained thirty-five for years.", author: "Oscar Wilde" },
  '36': { quote: "The purpose of life is not to be happy. It is to be useful, to be honorable, to be compassionate.", author: "Ralph Waldo Emerson" },
  '37': { quote: "In the middle of difficulty lies opportunity.", author: "Albert Einstein" },
  '38': { quote: "What lies behind us and what lies before us are tiny matters compared to what lies within us.", author: "Ralph Waldo Emerson" },
  '39': { quote: "The best is yet to come.", author: "Frank Sinatra" },
  '40': { quote: "Forty is the old age of youth; fifty is the youth of old age.", author: "Victor Hugo" },
  '41': { quote: "Forty-one: the year you stop caring what others think and start living for yourself.", author: "Unknown" },
  '42': { quote: "The answer to the ultimate question of life, the universe, and everything.", author: "Douglas Adams" },
  '43': { quote: "You are never too old to set another goal or to dream a new dream.", author: "C.S. Lewis" },
  '44': { quote: "The only limit to our realization of tomorrow is our doubts of today.", author: "Franklin D. Roosevelt" },
  '45': { quote: "At 45, life is just getting interesting.", author: "Unknown" },
  '46': { quote: "It is never too late to be what you might have been.", author: "George Eliot" },
  '47': { quote: "The secret of staying young is to live honestly, eat slowly, and lie about your age.", author: "Lucille Ball" },
  '48': { quote: "With age comes wisdom, but sometimes age comes alone.", author: "Oscar Wilde" },
  '49': { quote: "Forty-nine: one year to reflect before the next great chapter begins.", author: "Unknown" },
  '50': { quote: "At fifty, everyone has the face they deserve.", author: "George Orwell" },
  '51': { quote: "The afternoon knows what the morning never suspected.", author: "Robert Frost" },
  '52': { quote: "Fifty-two: one week for every year. Make each one count.", author: "Unknown" },
  '53': { quote: "Age is an issue of mind over matter. If you don't mind, it doesn't matter.", author: "Mark Twain" },
  '54': { quote: "The great use of life is to spend it for something that will outlast it.", author: "William James" },
  '55': { quote: "We turn not older with years, but newer every day.", author: "Emily Dickinson" },
  '56': { quote: "Fifty-six years of collecting moments worth remembering.", author: "Unknown" },
  '57': { quote: "Life is a succession of lessons which must be lived to be understood.", author: "Helen Keller" },
  '58': { quote: "The more you praise and celebrate your life, the more there is in life to celebrate.", author: "Oprah Winfrey" },
  '59': { quote: "Count your age by friends, not years. Count your life by smiles, not tears.", author: "John Lennon" },
  '60': { quote: "The great thing about getting older is that you don't lose all the other ages you've been.", author: "Madeleine L'Engle" },
  '61': { quote: "We don't stop playing because we grow old; we grow old because we stop playing.", author: "George Bernard Shaw" },
  '62': { quote: "Aging is not lost youth but a new stage of opportunity and strength.", author: "Betty Friedan" },
  '63': { quote: "The trick is growing up without growing old.", author: "Casey Stengel" },
  '64': { quote: "Sixty-four years of wisdom, and still learning something new every day.", author: "Unknown" },
  '65': { quote: "Retirement is not the end of the road. It is the beginning of the open highway.", author: "Unknown" },
  '66': { quote: "The wiser mind mourns less for what age takes away than what it leaves behind.", author: "William Wordsworth" },
  '67': { quote: "Age does not diminish the extreme disappointment of having a scoop of ice cream fall from the cone.", author: "Jim Fiebig" },
  '68': { quote: "Youth is the gift of nature, but age is a work of art.", author: "Stanislaw Jerzy Lec" },
  '69': { quote: "None are so old as those who have outlived enthusiasm.", author: "Henry David Thoreau" },
  '70': { quote: "I have reached an age when, if someone tells me to wear socks, I don't have to.", author: "Albert Einstein" },
  '71': { quote: "The longer I live, the more beautiful life becomes.", author: "Frank Lloyd Wright" },
  '72': { quote: "Seventy-two years around the sun, and still finding new reasons to smile.", author: "Unknown" },
  '73': { quote: "Old age is fifteen years older than I am.", author: "Oliver Wendell Holmes Sr." },
  '74': { quote: "Age is a case of mind over matter. If you don't mind, it don't matter.", author: "Satchel Paige" },
  '75': { quote: "The evening of a well-spent life brings its lamps with it.", author: "Joseph Joubert" },
  '76': { quote: "In youth we learn; in age we understand.", author: "Marie von Ebner-Eschenbach" },
  '77': { quote: "Wrinkles should merely indicate where the smiles have been.", author: "Mark Twain" },
  '78': { quote: "Do not grow old, no matter how long you live.", author: "Albert Einstein" },
  '79': { quote: "The soul has no age.", author: "Unknown" },
  '80': { quote: "To know how to grow old is the master work of wisdom.", author: "Henri-Frédéric Amiel" },
  '81': { quote: "Old age is like everything else. To make a success of it, you've got to start young.", author: "Theodore Roosevelt" },
  '82': { quote: "The key to successful aging is to pay as little attention to it as possible.", author: "Judith Regan" },
  '83': { quote: "Age is simply the number of years the world has been enjoying you.", author: "Unknown" },
  '84': { quote: "Growing old is mandatory; growing up is optional.", author: "Chili Davis" },
  '85': { quote: "Beautiful young people are accidents of nature, but beautiful old people are works of art.", author: "Eleanor Roosevelt" },
  '86': { quote: "The older I get, the greater power I seem to have to help the world.", author: "Susan B. Anthony" },
  '87': { quote: "I am not young enough to know everything.", author: "Oscar Wilde" },
  '88': { quote: "Life's tragedy is that we get old too soon and wise too late.", author: "Benjamin Franklin" },
  '89': { quote: "The years teach much which the days never know.", author: "Ralph Waldo Emerson" },
  '90': { quote: "At 90, you have seen it all, done most of it, and forgotten half.", author: "Unknown" },
  '91': { quote: "The secret of longevity is to keep breathing.", author: "Sophie Tucker" },
  '92': { quote: "I've learned that life is like a roll of toilet paper. The closer you get to the end, the faster it goes.", author: "Andy Rooney" },
  '93': { quote: "Old age is not so bad when you consider the alternative.", author: "Maurice Chevalier" },
  '94': { quote: "The first hundred years are the hardest.", author: "Wilson Mizner" },
  '95': { quote: "I still find each day too short for all the thoughts I want to think.", author: "John Burroughs" },
  '96': { quote: "Age is just a number. Life is what you make it.", author: "Unknown" },
  '97': { quote: "The more sand that has escaped from the hourglass of our life, the clearer we should see through it.", author: "Jean-Paul Sartre" },
  '98': { quote: "To be 98 is to have lived through nearly a century of human history.", author: "Unknown" },
  '99': { quote: "One year from a century. What a remarkable journey.", author: "Unknown" },
  '100': { quote: "A centenarian is living proof that life rewards those who show up.", author: "Unknown" },
  '101': { quote: "The first century was just practice.", author: "Unknown" },
  '102': { quote: "At 102, every sunrise is a standing ovation from the universe.", author: "Unknown" },
  '103': { quote: "Three digits and still counting blessings.", author: "Unknown" },
  '104': { quote: "Over a century of memories, and still making more.", author: "Unknown" },
  '105': { quote: "105 years young, with stories the history books forgot to mention.", author: "Unknown" },
  '106': { quote: "A life so long, it spans generations of wisdom.", author: "Unknown" },
  '107': { quote: "107 years of proof that the human spirit is limitless.", author: "Unknown" },
  '108': { quote: "Still here, still curious, still grateful.", author: "Unknown" },
  '109': { quote: "109 trips around the sun, and the view keeps getting better.", author: "Unknown" },
  '110': { quote: "A supercentenarian: rarer than a diamond, more precious than gold.", author: "Unknown" },
  'universal': [
    { quote: "The present moment is filled with joy and happiness. If you are attentive, you will see it.", author: "Thich Nhat Hanh" },
    { quote: "In the end, it's not the years in your life that count. It's the life in your years.", author: "Abraham Lincoln" },
    { quote: "Time you enjoy wasting is not wasted time.", author: "Marthe Troly-Curtin" },
  ]
};

const getQuoteForAge = (age) => {
  // Check for specific age quotes (30-90)
  const ageStr = String(age);
  if (QUOTES_BY_AGE[ageStr]) {
    return QUOTES_BY_AGE[ageStr];
  }
  // Fall back to universal for ages outside 30-90
  return QUOTES_BY_AGE['universal'][0];
};

const CreateCardPage = () => {
  const navigate = useNavigate();
  const [birthDate, setBirthDate] = useState(null);
  const [dateInput, setDateInput] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [accentColor, setAccentColor] = useState('#000000');
  const [inspirationalQuote, setInspirationalQuote] = useState(QUOTES_BY_AGE['universal'][0].quote);
  const [quoteAuthor, setQuoteAuthor] = useState(QUOTES_BY_AGE['universal'][0].author);
  const [previewScale, setPreviewScale] = useState(0.35);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [creatorEmail, setCreatorEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const printRef = useRef(null);
  const previewContainerRef = useRef(null);

  const weeksLived = calculateWeeksLived(birthDate);
  const weeksRemaining = 4680 - weeksLived;
  const age = calculateNextBirthdayAge(birthDate);

  // Calculate preview scale based on container width
  useEffect(() => {
    const updateScale = () => {
      if (previewContainerRef.current) {
        const containerWidth = previewContainerRef.current.offsetWidth;
        // A4 landscape width is 297mm ≈ 1122.5px at 96dpi
        const cardWidthPx = 1122.5;
        const scale = containerWidth / cardWidthPx;
        setPreviewScale(scale);
      }
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  // Handle date input change with auto-formatting and validation
  const handleDateInputChange = (value) => {
    // Remove non-numeric and non-slash characters
    let cleaned = value.replace(/[^\d/]/g, '');

    // Remove extra slashes — allow at most 2
    const parts = cleaned.split('/');
    if (parts.length > 3) {
      cleaned = parts.slice(0, 3).join('/');
    }

    // Limit total length
    if (cleaned.length > 10) {
      cleaned = cleaned.slice(0, 10);
    }

    setDateInput(cleaned);

    // Try to parse DD/MM/YYYY format
    if (cleaned.length >= 8) {
      const parsed = parse(cleaned, 'dd/MM/yyyy', new Date());
      if (isValid(parsed) && parsed <= new Date() && parsed >= new Date('1920-01-01')) {
        setBirthDate(parsed);
        // Update quote based on next birthday age
        const newAge = calculateNextBirthdayAge(parsed);
        const suggestedQuote = getQuoteForAge(newAge);
        setInspirationalQuote(suggestedQuote.quote);
        setQuoteAuthor(suggestedQuote.author);
      } else {
        setBirthDate(null);
      }
    } else {
      setBirthDate(null);
    }
  };

  const handleReceivePDF = () => {
    if (!birthDate) {
      toast.error('Please enter a birthdate first');
      return;
    }
    setShowEmailModal(true);
  };

  const generatePDFBase64 = async () => {
    const printElement = printRef.current;
    if (!printElement) throw new Error('Print element not found');

    const page = printElement.firstChild;
    
    const canvas = await html2canvas(page, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#FDFBF7',
      logging: false,
      width: page.offsetWidth,
      height: page.offsetHeight
    });

    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    pdf.addImage(imgData, 'JPEG', 0, 0, 297, 210);

    // Return as base64
    return pdf.output('datauristring').split(',')[1];
  };

  const sendCardToEmail = useCallback(async () => {
    if (!creatorEmail || !birthDate) {
      toast.error('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(creatorEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSending(true);
    toast.loading('Generating and sending your card...', { id: 'sending' });

    try {
      // Generate PDF as base64
      const pdfBase64 = await generatePDFBase64();

      // Send to backend
      const response = await fetch(`${API_URL}/api/send-card`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          creator_email: creatorEmail,
          recipient_name: recipientName || 'Dear Friend',
          recipient_birthdate: dateInput,
          recipient_age: age,
          weeks_lived: weeksLived,
          weeks_remaining: weeksRemaining,
          accent_color: accentColor,
          inspirational_quote: inspirationalQuote,
          quote_author: quoteAuthor,
          pdf_base64: pdfBase64
        })
      });

      // Clone before reading to avoid "body stream already read" from interceptors
      const cloned = response.clone();
      let data;
      try {
        data = await cloned.json();
      } catch (e) {
        const text = await response.text();
        throw new Error(text || 'Failed to send email');
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send email');
      }

      toast.success('Card sent to your email!', { id: 'sending' });
      setShowEmailModal(false);
      setCreatorEmail('');
    } catch (error) {
      console.error('Send card error:', error);
      toast.error(error.message || 'Failed to send card. Please try again.', { id: 'sending' });
    } finally {
      setIsSending(false);
    }
  }, [creatorEmail, birthDate, dateInput, recipientName, age, weeksLived, weeksRemaining, accentColor, inspirationalQuote, quoteAuthor]);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Toaster position="top-center" richColors />
      
      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div 
            className="bg-[#FDFBF7] rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
            data-testid="email-modal"
          >
            <div className="p-6 border-b border-[#E5E5E5]">
              <div className="flex justify-between items-center">
                <h2 
                  className="text-xl text-[#1A1A1A]"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Receive Your Card
                </h2>
                <button 
                  onClick={() => setShowEmailModal(false)}
                  className="text-[#737373] hover:text-[#1A1A1A] transition-colors"
                  data-testid="close-modal-btn"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <p className="text-sm text-[#737373] mt-2">
                Enter your email address and we'll send your card as a PDF.
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-[0.2em] text-[#737373] font-medium">
                  Your Email Address
                </label>
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={creatorEmail}
                  onChange={(e) => setCreatorEmail(e.target.value)}
                  className="bg-white border border-[#E5E5E5] rounded-lg px-4 py-3 h-auto text-base focus:ring-2 focus:ring-[#9A3B3B]/20 focus:border-[#9A3B3B] placeholder:text-[#737373]/50 hover:border-[#1A1A1A] transition-all"
                  data-testid="email-input"
                  onKeyDown={(e) => e.key === 'Enter' && sendCardToEmail()}
                />
              </div>
              
              <Button 
                onClick={sendCardToEmail}
                disabled={isSending || !creatorEmail}
                className="w-full rounded-full px-6 py-4 text-base font-medium bg-[#1A1A1A] text-[#FDFBF7] hover:bg-[#333333] transition-all duration-300 disabled:opacity-50"
                data-testid="send-email-btn"
              >
                <Mail className="h-5 w-5 mr-2" />
                {isSending ? 'Sending...' : 'Send to My Email'}
              </Button>
              
              <p className="text-xs text-center text-[#737373]">
                The PDF will be sent to your email with printing instructions.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-sm border-b border-[#E5E5E5]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-3 sm:py-4 flex justify-between items-center">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#1A1A1A] hover:text-[#9A3B3B] transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="hidden sm:inline">Back</span>
          </button>
          <h1 
            className="text-lg sm:text-2xl text-[#1A1A1A]"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Life in Weeks
          </h1>
          <Button 
            onClick={handleReceivePDF}
            disabled={!birthDate || isGenerating}
            className="rounded-full px-3 sm:px-6 py-2 text-sm sm:text-base bg-[#1A1A1A] text-[#FDFBF7] hover:bg-[#333333] transition-all duration-300 disabled:opacity-50"
            data-testid="receive-btn"
          >
            <Mail className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Receive PDF</span>
          </Button>
        </div>
      </nav>

      <div className="pt-20 sm:pt-24 pb-12 px-4 md:px-8 lg:px-12">
        <div className="max-w-[1600px] mx-auto">
          {/* Mobile: Inputs first, preview last. Desktop: side by side with larger preview */}
          <div className="flex flex-col lg:grid lg:grid-cols-12 gap-6 lg:gap-8">
            
            {/* Card Preview - Takes 8 columns on desktop, shows last on mobile */}
            <div className="order-2 lg:order-2 lg:col-span-8 lg:sticky lg:top-24 space-y-3" data-testid="preview-panel">
              <p className="text-xs uppercase tracking-[0.3em] text-[#737373]">
                Card Preview
              </p>
              
              {/* Preview Container - Responsive scaling */}
              <div 
                ref={previewContainerRef}
                className="bg-white rounded-xl shadow-lg border border-[#E5E5E5]/50 overflow-hidden"
                data-testid="card-preview"
              >
                <div 
                  style={{
                    transform: `scale(${previewScale})`,
                    transformOrigin: 'top left',
                    width: '297mm',
                    height: '210mm',
                    marginBottom: `calc(210mm * ${previewScale} - 210mm)`
                  }}
                >
                  <PrintableCardV2
                    birthDate={birthDate}
                    recipientName={recipientName}
                    inspirationalQuote={inspirationalQuote}
                    quoteAuthor={quoteAuthor}
                    accentColor={accentColor}
                  />
                </div>
              </div>

              {/* Panel labels - matches new order: Cover | Message | Grid */}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="text-[10px] uppercase tracking-wider text-[#737373]">
                  Cover
                </div>
                <div className="text-[10px] uppercase tracking-wider text-[#737373]">
                  Message Space
                </div>
                <div className="text-[10px] uppercase tracking-wider text-[#737373]">
                  The Grid
                </div>
              </div>

              {/* Receive PDF Button (Mobile) */}
              <div className="lg:hidden pt-4">
                <Button 
                  onClick={handleReceivePDF}
                  disabled={!birthDate || isGenerating}
                  className="w-full rounded-full px-8 py-6 text-lg font-medium bg-[#1A1A1A] text-[#FDFBF7] hover:bg-[#333333] transition-all duration-300 disabled:opacity-50"
                  data-testid="mobile-receive-btn"
                >
                  <Mail className="h-5 w-5 mr-2" />
                  Receive PDF
                </Button>
              </div>
            </div>

            {/* Left Panel - Inputs - Takes 4 columns on desktop, shows first on mobile */}
            <div className="order-1 lg:order-1 lg:col-span-4 space-y-6 sm:space-y-8" data-testid="input-panel">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-[#737373] mb-2">
                  Create your card
                </p>
                <h1 
                  className="text-2xl md:text-3xl text-[#1A1A1A] mb-2"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Life in Weeks
                </h1>
                <p className="text-sm text-[#737373]">
                  A personalized birthday card that puts life into perspective.
                </p>
              </div>

              {/* Recipient Name Input */}
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-[0.2em] text-[#737373] font-medium">
                  Recipient's Name
                </label>
                <Input
                  type="text"
                  placeholder="Enter name..."
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  className="bg-white border border-[#E5E5E5] rounded-lg px-4 py-4 h-auto text-lg focus:ring-2 focus:ring-[#9A3B3B]/20 focus:border-[#9A3B3B] placeholder:text-[#737373]/50 hover:border-[#1A1A1A] transition-all"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                  data-testid="name-input"
                />
              </div>

              {/* Birthdate Input */}
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-[0.2em] text-[#737373] font-medium">
                  Birthdate <span className="normal-case tracking-normal font-normal text-[#737373]/50">DD/MM/YYYY</span>
                </label>
                <Input
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={dateInput}
                  onChange={(e) => handleDateInputChange(e.target.value)}
                  maxLength={10}
                  className={cn(
                    "bg-white border border-[#E5E5E5] rounded-lg px-4 py-4 h-auto text-lg focus:ring-2 focus:ring-[#9A3B3B]/20 focus:border-[#9A3B3B] placeholder:text-[#737373]/50 hover:border-[#1A1A1A] transition-all",
                    birthDate && "border-green-500"
                  )}
                  style={{ fontFamily: "'Inter', sans-serif" }}
                  data-testid="date-input"
                />
                {birthDate && (
                  <p className="text-sm text-[#737373]">
                    {format(birthDate, "EEEE, MMMM d, yyyy")}
                  </p>
                )}
              </div>

              {/* Stats Display */}
              {birthDate && (
                <div className="pt-6 sm:pt-8 border-t border-[#E5E5E5] fade-in" data-testid="stats-display">
                  <div className="grid grid-cols-3 gap-3 sm:gap-6">
                    <div className="text-center">
                      <p 
                        className="text-2xl sm:text-3xl md:text-4xl text-[#1A1A1A] mb-1"
                        style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
                      >
                        {age}
                      </p>
                      <p className="text-[8px] sm:text-[10px] uppercase tracking-[0.15em] sm:tracking-[0.2em] text-[#737373]">
                        Turning
                      </p>
                    </div>
                    <div className="text-center">
                      <p 
                        className="text-2xl sm:text-3xl md:text-4xl text-[#1A1A1A] mb-1"
                        style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
                      >
                        {weeksLived.toLocaleString()}
                      </p>
                      <p className="text-[8px] sm:text-[10px] uppercase tracking-[0.15em] sm:tracking-[0.2em] text-[#737373]">
                        Weeks Lived
                      </p>
                    </div>
                    <div className="text-center">
                      <p 
                        className="text-2xl sm:text-3xl md:text-4xl mb-1"
                        style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300, color: accentColor }}
                      >
                        {weeksRemaining.toLocaleString()}
                      </p>
                      <p className="text-[8px] sm:text-[10px] uppercase tracking-[0.15em] sm:tracking-[0.2em] text-[#737373]">
                        Weeks Ahead
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Editable Quote - Now under the age stats */}
              {birthDate && (
                <div className="pt-6 border-t border-[#E5E5E5] space-y-3 fade-in" data-testid="editable-text">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#737373] font-medium">
                    Quote
                  </p>
                  
                  <div className="space-y-2">
                    <textarea
                      value={inspirationalQuote}
                      onChange={(e) => setInspirationalQuote(e.target.value)}
                      rows={2}
                      className="w-full bg-white border border-[#E5E5E5] rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#9A3B3B]/20 focus:border-[#9A3B3B] placeholder:text-[#737373]/50 hover:border-[#1A1A1A] transition-all resize-none"
                      style={{ fontFamily: "'Georgia', serif" }}
                      data-testid="quote-input"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">
                      Quote Attribution
                    </label>
                    <Input
                      type="text"
                      value={quoteAuthor}
                      onChange={(e) => setQuoteAuthor(e.target.value)}
                      className="bg-white border border-[#E5E5E5] rounded-lg px-3 py-2 h-auto text-sm focus:ring-2 focus:ring-[#9A3B3B]/20 focus:border-[#9A3B3B] hover:border-[#1A1A1A] transition-all"
                      data-testid="author-input"
                    />
                  </div>
                </div>
              )}

              {/* Color Selection */}
              {birthDate && (
                <div className="pt-6 border-t border-[#E5E5E5] fade-in" data-testid="color-selection">
                  <div className="flex items-center gap-2 mb-4">
                    <Palette className="h-4 w-4 text-[#737373]" />
                    <p className="text-xs uppercase tracking-[0.2em] text-[#737373] font-medium">
                      Accent Color
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    {COLOR_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => setAccentColor(option.color)}
                        className={cn(
                          "flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 rounded-lg border-2 transition-all text-sm",
                          accentColor === option.color 
                            ? "border-[#1A1A1A] bg-[#1A1A1A]/5" 
                            : "border-[#E5E5E5] hover:border-[#1A1A1A]/50"
                        )}
                        data-testid={`color-${option.id}`}
                      >
                        <div 
                          className="w-5 h-5 rounded-full border border-[#E5E5E5]"
                          style={{ backgroundColor: option.color }}
                        />
                        <span className="text-sm text-[#1A1A1A]">{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Instructions */}
              <div className="pt-6 border-t border-[#E5E5E5]">
                <p className="text-xs uppercase tracking-[0.2em] text-[#737373] font-medium mb-3">
                  Print & Fold Instructions
                </p>
                <div className="bg-white rounded-xl p-4 border border-[#E5E5E5]/50 space-y-2 text-sm text-[#737373]">
                  <p><strong className="text-[#1A1A1A]">1.</strong> Print on A4 landscape (200-300gsm cardstock recommended)</p>
                  <p><strong className="text-[#1A1A1A]">2.</strong> Fold message panel over the grid along second dashed line</p>
                  <p><strong className="text-[#1A1A1A]">3.</strong> Fold cover panel back over the message along first dashed line</p>
                  <p><strong className="text-[#1A1A1A]">4.</strong> Result: Cover pulls out to reveal message and grid</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden Printable Card for PDF Generation */}
      <div className="fixed left-[-9999px] top-0" aria-hidden="true">
        <PrintableCardV2 
          ref={printRef}
          birthDate={birthDate}
          recipientName={recipientName}
          inspirationalQuote={inspirationalQuote}
          quoteAuthor={quoteAuthor}
          accentColor={accentColor}
        />
      </div>
    </div>
  );
};

export default CreateCardPage;
